import React, { useState } from 'react';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import { WorkoutTypeRuleDto } from '../../core/api/FitnessActivityClient';
import { injectTSDI } from '../../core/tsdi';
import { Api } from '../../core/api';

interface DeleteWorkoutTypeRuleDialogProps {
    open: boolean;
    onClose: () => void;
    rule: WorkoutTypeRuleDto | null;
    onSuccess: () => void;
}

function formatEnumLabel(value?: string | null): string {
    if (!value) {
        return 'Unknown';
    }

    return value
        .toLowerCase()
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

function getRuleDescription(rule: WorkoutTypeRuleDto | null): string {
    if (!rule) {
        return 'this rule';
    }

    const parts: string[] = [];
    if (rule.workoutCategory) {
        parts.push(`category "${formatEnumLabel(rule.workoutCategory)}"`);
    }
    if (rule.workoutType) {
        parts.push(`type "${formatEnumLabel(rule.workoutType)}"`);
    }

    return parts.length > 0 ? parts.join(' and ') : `rule #${rule.id}`;
}

export const DeleteWorkoutTypeRuleDialog: React.FC<
    DeleteWorkoutTypeRuleDialogProps
> = ({ open, onClose, rule, onSuccess }) => {
    const { fitnessActivityClient } = injectTSDI(Api);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleDelete = async () => {
        if (!rule?.id) return;

        setLoading(true);
        setError(null);

        try {
            await fitnessActivityClient.admin.workoutTypeRuleControllerDelete(
                rule.id
            );
            onSuccess();
            onClose();
        } catch (err) {
            console.error('Failed to delete workout validation rule:', err);
            setError(
                'Failed to delete workout validation rule. Please try again.'
            );
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setError(null);
            onClose();
        }
    };

    return (
        <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Delete Workout Validation Rule</DialogTitle>
                </DialogHeader>
                <div className="flex flex-col gap-2">
                    <p className="text-sm">
                        Are you sure you want to delete the{' '}
                        {getRuleDescription(rule)}?
                    </p>
                    <p className="text-sm text-muted-foreground">
                        This action cannot be undone. All validation rule data
                        will be permanently removed.
                    </p>

                    {error && (
                        <Alert variant="destructive">
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}
                </div>
                <DialogFooter>
                    <Button
                        variant="ghost"
                        onClick={handleClose}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={handleDelete}
                        loading={loading}
                    >
                        Delete
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
